import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { FeatureSelectService, LayerStylesService, MapService } from '@ebw-monorepo/map';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalService } from '@ebw-monorepo/modal';
import { DebounceUtility } from '@ebw-monorepo/utils';
import { firstValueFrom } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../../../utils/src/lib/services/api-url.service';
import { FeatureViewerService } from '../../../services/feature-viewer.service';
import { FilesystemService } from '../../../services/filesystem.service';
import { PsaService } from '../../../services/psa.service';
import { isChecklistElementType } from '../../../utils/checklist.utils';
import { CheckListComponent } from './check-list/check-list.component';
import { DetailActionComponent } from './detail-action/detail-action.component';
import { DetailEventComponent } from './detail-event/detail-event.component';
import { DetailNodeComponent } from './detail-node/detail-node.component';
import {
  EliminazioneBloccataData,
  EliminazioneBloccataDialogComponent,
  SezioneCollegamenti
} from './eliminazione-bloccata-dialog/eliminazione-bloccata-dialog.component';
import { Actions, PsaConfigurator } from './psa-configurator.model';

/**
 * @author Tommaso Lazzaretti
 *
 * Component responsible for handling the main PSA (Piano sicurezza acque) interface.
 * This component provides tabs for different PSA entities (Elements, Events, Actions)
 * and allows users to filter, view details, and modify these entities.
 * It also integrates with a debouncing utility to manage search queries efficiently.
 *
 * @class PsaComponent
 * @implements {OnInit, OnDestroy}
 */
@Component({
  selector: 'ebw-monorepo-psa',
  templateUrl: './psa.component.html',
  styleUrls: ['./psa.component.scss']
})
export class PsaComponent implements OnInit, OnDestroy {
  @ViewChild('tooltipMenu') tooltipMenu: MatMenuTrigger | undefined;
  /**
   * Index of the tab to be selected initially.
   * This value is passed as an input to the component.
   * @type {number}
   * @default 0
   */
  @Input() selectedTab = 0;
  private _debounceUtility = new DebounceUtility(300); // 300 è il tempo di debounce in millisecondi

  filterTerm: string = null;
  formSelectedTab: FormControl;
  /** Structure of the tabs present */
  visibleTabs: PsaConfigurator[] = [];

  listElements: any[] = null;
  typesList: { type: string; tipologia: string }[] = null;
  /** Element types with a checklist: the "Checklist" menu item is shown only for elements of these types */
  checklistElementTypes: string[] = [];
  selectedType: string = null;

  geoFeatureStyle = null;
  geoFeatureStyleHighlight = null;

  tooltipTimer: any;

  constructor(
    private dialog: MatDialog,
    private _psaService: PsaService,
    private _layerStyleService: LayerStylesService,
    private _mapService: MapService,
    private _featureSelectService: FeatureSelectService,
    private _modalService: EbwModalService,
    private _translateService: I18NService,
    private filesystemService: FilesystemService,
    private _apiUrlService: ApiUrlService,
    private _featureViewerService: FeatureViewerService
  ) {
    this.visibleTabs = [
      {
        tab_id: 'aree_psa',
        tab_name: 'Aree PSA',
        actions: [Actions.CENTER_MAP, Actions.EVIDENCE, Actions.DOWNLOAD_MATRICE_RISCHIO]
      },
      {
        tab_id: 'Elementi',
        tab_name: 'Elementi',
        actions: [Actions.DETAIL, Actions.CENTER_MAP, Actions.EVIDENCE]
      },
      {
        tab_id: 'Eventi',
        tab_name: 'Eventi',
        actions: [Actions.MODIFY, Actions.DELETE]
      },
      {
        tab_id: 'Azioni',
        tab_name: 'Azioni',
        actions: [Actions.MODIFY, Actions.DELETE]
      }
    ];
  }

  ngOnInit(): void {
    this._psaService._getChecklistElementTypes().subscribe((elementTypes) => (this.checklistElementTypes = elementTypes));
    this.geoFeatureStyle = this._layerStyleService.buildFeatureStyleFromId('SelectionStyle');
    this.geoFeatureStyleHighlight = this._layerStyleService.buildFeatureStyleFromId('HighlightFeatureFill');

    this.formSelectedTab = new FormControl(this.selectedTab);

    this._debounceUtility.debouncedSubject.subscribe((value) => {
      switch (this.selectedTab) {
        case 0:
          this._psaService._getListAreas(value).subscribe((value) => {
            this.listElements = value;
          });
          break;
        case 1:
          if (value.length >= 3)
            this._psaService._getListElements(value, this.selectedType).subscribe((value) => {
              this.listElements = value;
            });
          break;
        case 2:
          this._psaService._getListEvents({ nome: value, tipologia: this.selectedType }).subscribe((value) => {
            this.listElements = value;
          });
          break;
        case 3:
          this._psaService._getListActions(value).subscribe((value) => {
            this.listElements = value;
          });
          break;
        default:
          break;
      }
    });

    this._psaService._getListAreas().subscribe((value) => {
      this.listElements = value;
    });

    this._psaService._getEntityTypes().subscribe((value) => {
      this.typesList = value;
    });

    // se un nodo viene creato/modificato/eliminato altrove (es. dal pannello mappa), aggiorno la lista di ricerca se è quella attiva
    this._psaService.nodeChanged$.subscribe(() => {
      if (this.selectedTab === 1 && this.filterTerm) {
        this.onInputChange();
      }
    });
  }

  changeElementType(_) {
    this.filterTerm = '';
    this.listElements = null;
  }

  showDetail(item) {
    const featureCollection = {
      type: 'FeatureCollection',
      features: [
        {
          id: item.urn,
          type: 'Feature',
          geometry: safeJsonClone(item.geom),
          properties: {
            ...item
          }
        }
      ]
    };
    this._featureSelectService.featuresSelected = [];
    this._featureSelectService.parseSelectedFeatures(featureCollection, this._apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URN));
    this._featureSelectService.notifyFeaturesSelectedChange();
  }

  centerOnMap(item) {
    this._mapService.clearAllFeaturesFromTemporaryLayer(true);

    const clone = safeJsonClone(item);
    clone.geom.coordinates = this._mapService.transformGeometryCordsFrom4326To3857(clone.geom.coordinates);
    this._mapService.addFeatureToTemporaryLayer(clone.geom, this.geoFeatureStyle, true, false);
    this._mapService.centerMapOnGeometry(clone.geom);
  }

  evidence(item) {
    this._mapService.clearAllFeaturesFromTemporaryLayer(true);

    const clone = safeJsonClone(item);
    clone.geom.coordinates = this._mapService.transformGeometryCordsFrom4326To3857(clone.geom.coordinates);
    this._mapService.addFeatureToTemporaryLayer(clone.geom, this.geoFeatureStyleHighlight, true, false);
    this._mapService.centerMapOnGeometry(clone.geom);
  }

  async removeItem(item): Promise<any> {
    let content = this._translateService.getInstantValue('map.sidebar.psa.actions.delete_message');

    if (this.formSelectedTab.value === 2) {
      const verifica = await this._verificaEliminazioneEvento(item);
      if (verifica.bloccata) {
        return;
      }
      if (verifica.azioniCollegate.length) {
        content += `\n\n${this._avvisoAzioniCollegate(verifica.azioniCollegate)}`;
      }
    }

    if (this.formSelectedTab.value === 3) {
      const avviso = await this._avvisoEventiCollegati(item);
      if (avviso) {
        content += `\n\n${avviso}`;
      }
    }

    const title = this._translateService.getInstantValue('map.sidebar.psa.actions.delete');
    const removeAskModal = this._modalService.openConfirmModal({ title, content }).afterClosed();
    // COnferma di eliminazione
    const askResult = await firstValueFrom(removeAskModal);
    if (!askResult) {
      return;
    }

    enum Action {
      DeleteEvent = 2,
      DeleteAction = 3
    }

    const selectedTab = this.formSelectedTab.value;

    try {
      let observable;
      switch (selectedTab) {
        case Action.DeleteEvent:
          observable = this._psaService._deleteEvent(item.id);
          break;
        case Action.DeleteAction:
          observable = this._psaService._deleteAction(item.id);
          break;
        default:
          break;
      }

      if (!observable) {
        return;
      }
      await firstValueFrom(observable);
      this.getLists();
    } catch (error) {
      const title = this._translateService.getInstantValue('map.sidebar.psa.actions.delete');
      const content = error?.error?.message ?? error?.message;
      this._modalService.openErrorModal({ title, content });
    }
  }

  /**
   * Un evento collegato a elementi di rete non si puo' eliminare: invece dell'errore generico del backend
   * mostra quali elementi lo impediscono. Restituisce anche le azioni del catalogo collegate, i cui
   * collegamenti verranno rimossi insieme all'evento.
   */
  private async _verificaEliminazioneEvento(item): Promise<{ bloccata: boolean; azioniCollegate: string[] }> {
    let evento: any;
    let azioni: any[];
    try {
      [evento, azioni] = await Promise.all([
        firstValueFrom(this._psaService._getEvent({ id: item.id })),
        firstValueFrom(this._psaService._getEventActions(item.id))
      ]);
    } catch (error) {
      // senza il dettaglio si procede col flusso normale: il backend blocca comunque l'eliminazione
      console.error('[PsaComponent][_verificaEliminazioneEvento]', error);
      return { bloccata: false, azioniCollegate: [] };
    }

    const ordina = (voci: string[]) => [...voci].sort((a, b) => a.localeCompare(b, 'it', { numeric: true }));
    const azioniCollegate = ordina((azioni ?? []).map((a) => a.azione?.nome ?? ''));
    const elementi: any[] = evento?.entities ?? [];
    if (!elementi.length) {
      return { bloccata: false, azioniCollegate };
    }

    const t = (chiave: string) => this._translateService.getInstantValue(`map.sidebar.psa.delete_blocked.${chiave}`);
    const perTipologia = new Map<string, string[]>();
    for (const elemento of elementi) {
      const tipologia = elemento.schema_idropotabile?.tipologia || t('no_type');
      perTipologia.set(tipologia, [
        ...(perTipologia.get(tipologia) ?? []),
        elemento.schema_idropotabile?.search_term ?? `#${elemento.schema_idropotabile_id}`
      ]);
    }
    const sezioni: SezioneCollegamenti[] = [
      {
        titolo: t('elements'),
        suggerimento: t('elements_hint'),
        gruppi: [...perTipologia.keys()].sort().map((tipologia) => ({ etichetta: tipologia, voci: ordina(perTipologia.get(tipologia)) }))
      }
    ];

    const data: EliminazioneBloccataData = {
      titolo: t('event_title'),
      messaggio: this._translateService.getTranslateParameter('map.sidebar.psa.delete_blocked.event_message', item.nome),
      sezioni
    };
    await firstValueFrom(this.dialog.open(EliminazioneBloccataDialogComponent, { width: '50vw', maxWidth: '100vw', data }).afterClosed());
    return { bloccata: true, azioniCollegate };
  }

  // i collegamenti con gli eventi del catalogo vengono eliminati insieme all'azione: l'utente va avvisato prima di confermare
  private async _avvisoEventiCollegati(item): Promise<string> {
    const azione = await firstValueFrom(this._psaService._getAction({ id: item.id }));
    const eventi = azione?.events?.length ?? 0;
    if (!eventi) {
      return '';
    }
    return eventi === 1
      ? this._translateService.getInstantValue('map.sidebar.psa.delete_blocked.events_warning_one')
      : this._translateService.getTranslateParameter('map.sidebar.psa.delete_blocked.events_warning', String(eventi));
  }

  private _avvisoAzioniCollegate(azioni: string[]): string {
    const massimo = 5;
    // il messaggio della conferma viene reso come innerHTML: i nomi inseriti dagli utenti vanno resi come testo
    const comeTesto = (valore: string) => valore.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const elenco = azioni.slice(0, massimo).map((nome) => `• ${comeTesto(nome)}`);
    if (azioni.length > massimo) {
      elenco.push(this._translateService.getTranslateParameter('map.sidebar.psa.delete_blocked.others', String(azioni.length - massimo)));
    }
    const intestazione = this._translateService.getTranslateParameter('map.sidebar.psa.delete_blocked.actions_warning', String(azioni.length));
    return `${intestazione}\n${elenco.join('\n')}`;
  }

  goToCheckList(item) {
    const ref = this.dialog.open(CheckListComponent, {
      maxWidth: '100vw',
      maxHeight: '100%',
      width: '85%',
      data: item.id,
      hasBackdrop: false,
      autoFocus: false,
      restoreFocus: false
    });

    ref.afterClosed().subscribe();
  }

  modifyItem(item) {
    this.onCreateNew(item);
  }

  onInputChange(): void {
    this._debounceUtility.trigger(this.filterTerm);
  }

  /**
   * Handles tab change events. Resets the filter term and selected list when a new tab is selected.
   * @param {any} event - The tab change event.
   * @returns {void}
   */
  onTabChange(event): void {
    this.filterTerm = null;
    this.listElements = null;
    this.formSelectedTab.setValue(event);
    this.selectedTab = event;
    this.selectedType = null;
    this.getLists();
  }

  /**
   * Retrieves the list of items (Elements, Events, or Actions) for the current tab.
   * This method is called when switching tabs or after creating/editing an item.
   * @private
   * @returns {void}
   */
  private getLists() {
    switch (this.selectedTab) {
      case 0:
        this._psaService._getListAreas().subscribe((value) => {
          this.listElements = value;
        });
        break;
      case 1:
        // non carico direttamente la lista perchè potrei avere troppi elementi
        // meglio eseguire la ricerca dopo aver inserito una stringa di ricerca
        break;
      case 2:
        this._psaService._getListEvents({ nome: this.filterTerm ? this.filterTerm : '', tipologia: this.selectedType }).subscribe((value) => {
          this.listElements = value;
        });
        break;
      case 3:
        this._psaService._getListActions(this.filterTerm ? this.filterTerm : '').subscribe((value) => {
          this.listElements = value;
        });
        break;
      default:
        break;
    }
  }

  /**
   * Opens a modal dialog for creating or editing an item (Event or Action).
   * @param {any} [item] - The item to be edited (if applicable). If no item is provided, a new item will be created.
   * @returns {void}
   */
  onCreateNew(item?): void {
    let component;
    switch (this.formSelectedTab.value) {
      case 1:
        // CREATE new nodes
        component = DetailNodeComponent;
        break;
      case 2:
        // CREATE new elements
        component = DetailEventComponent;
        break;
      case 3:
        // CREATE new actions
        component = DetailActionComponent;
        break;
      default:
        break;
    }

    // il dialog del nodo usa il click sulla mappa per le coordinate: se c'è già uno strumento aperto non si apre.
    // Il rilascio avviene nella destroy del dialog
    if (
      component === DetailNodeComponent &&
      !this._mapService.acquireMapClick({ id: DetailNodeComponent.CLICK_OWNER_ID, level: 'strong' })
    ) {
      return;
    }

    const ref = this.dialog.open(component, {
      width: '50vw',
      maxWidth: '100vw',
      maxHeight: '100%',
      data: item ? item : null,
      // per il nodo la mappa deve restare cliccabile sotto il dialog (click su mappa per le coordinate);
      // senza backdrop MatDialog chiuderebbe comunque il dialog al click fuori, quindi serve anche disableClose
      hasBackdrop: this.formSelectedTab.value !== 1,
      disableClose: this.formSelectedTab.value === 1,
      // nell'evento il primo elemento focalizzabile è una checkbox dei pericoli, che apparirebbe selezionata
      autoFocus: this.formSelectedTab.value === 2 ? false : 'first-tabbable'
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp) {
        this.getLists();
        // solo gli oggetti sulla mappa (nodi, elementi) hanno una geometria da selezionare, gli eventi no
        if (resp?.id && resp?.geom) {
          this.showDetail(resp);
        }
      }
    });
  }

  /**
   * Exports the risk matrix to an Excel file.
   * Non usiamo window.open() perché ha limiti di dimensione
   * sugli URL e può essere bloccato dal browser.
   * Utilizziamo invece Blob + <a> temporaneo per una gestione più efficiente.
   */
  openMatriceRischioTotale(item: any, downloadInFilesystem: boolean) {
    this._psaService.openMatriceRischio(item, downloadInFilesystem, true);
  }

  openFilesystem() {
    this.filesystemService.openFilesystemModal();
  }

  /**
   * Function used by Angular's ngFor directive to optimize rendering by tracking items by a unique identifier.
   * This prevents Angular from re-rendering the entire list when only a few elements change.
   *
   * @param {number} index - The index of the item in the list.
   * @param {any} item - The current item in the list.
   * @returns {any} The unique identifier for the item, typically an ID or another unique property.
   */
  trackByFn(index: number, item: any): any {
    return item.id; // Replace 'id' with the unique property of your items.
  }

  /**
   * Un evento puo' essere associato a piu' tipologie di elemento, ognuna con la propria gravita':
   * qui le elenco per la riga della lista eventi.
   */
  tipologieLabel(item: any): string {
    return (item.tipologie || []).map((t: any) => t.tipologia).join(', ');
  }

  toggleTooltip(menuTrigger: MatMenuTrigger) {
    menuTrigger.menuOpen ? menuTrigger.openMenu() : menuTrigger.closeMenu();
  }

  ngOnDestroy(): void {
    this._debounceUtility.complete();
    this._mapService.clearAllFeaturesFromTemporaryLayer(true);
    // uscendo dal PSA (altro strumento o chiusura del pannello) non resta aperto il dettaglio di un suo elemento
    this._featureViewerService.close();
  }

  /**
   * Exposes the Actions enum for use in the template.
   * @readonly
   */
  protected readonly Actions = Actions;

  hasChecklist(item: { tipologia?: string }): boolean {
    return isChecklistElementType(item?.tipologia, this.checklistElementTypes);
  }
}
